import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Plus, Trash2, Check, ExternalLink, Package, CheckCircle2, DollarSign } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function Pedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    enlace_producto: '',
    nombre_producto: '',
    cantidad: 1,
    precio: ''
  });

  useEffect(() => {
    fetchPedidos();
  }, []);

  const fetchPedidos = async () => {
    try {
      const res = await axios.get(`${API}/pedidos`);
      setPedidos(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.enlace_producto || !formData.precio) {
      alert('Rellena el enlace y el precio');
      return;
    }
    try {
      await axios.post(`${API}/pedidos`, {
        ...formData,
        precio: parseFloat(formData.precio)
      });
      setShowDialog(false);
      setFormData({ enlace_producto: '', nombre_producto: '', cantidad: 1, precio: '' });
      fetchPedidos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCompletar = async (id) => {
    try {
      await axios.put(`${API}/pedidos/${id}/completar`);
      fetchPedidos();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleTogglePagado = async (id) => {
    try {
      await axios.put(`${API}/pedidos/${id}/pagado`);
      fetchPedidos();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleCompletarTodos = async () => {
    if (!window.confirm('¿Marcar todos los pedidos pendientes como completados?')) return;
    try {
      await axios.put(`${API}/pedidos/completar-todos`);
      fetchPedidos();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este pedido?')) return;
    try {
      await axios.delete(`${API}/pedidos/${id}`);
      fetchPedidos();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const pedidosPendientes = pedidos.filter(p => p.estado === 'pendiente');
  const pedidosCompletados = pedidos.filter(p => p.estado === 'completado');
  const totalPendiente = pedidosPendientes.reduce((acc, p) => acc + (p.precio * p.cantidad), 0);
  const totalPagado = pedidosPendientes.filter(p => p.pagado).reduce((acc, p) => acc + (p.precio * p.cantidad), 0);
  const totalSinPagar = totalPendiente - totalPagado;

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gold flex items-center gap-2">
              <ShoppingCart className="h-8 w-8" /> Pedidos Prozis
            </h1>
            <p className="text-gray-400">Solicita tus suplementos y los pedimos en grupo</p>
          </div>
          <Button className="btn-primary-mk" onClick={() => setShowDialog(true)} data-testid="btn-nuevo-pedido">
            <Plus className="h-5 w-5 mr-2" /> Nuevo Pedido
          </Button>
        </div>

        {/* Resumen para entrenador */}
        {user?.role === 'entrenador' && pedidosPendientes.length > 0 && (
          <Card className="bg-gradient-to-r from-green-900 to-emerald-800 border-green-600 mb-6">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-green-300 text-sm">Pedidos pendientes</p>
                  <p className="text-3xl font-bold text-white">{pedidosPendientes.length} pedidos</p>
                  <div className="flex gap-4 mt-1">
                    <p className="text-green-300">Total: <span className="text-white font-bold">{totalPendiente.toFixed(2)}€</span></p>
                    <p className="text-green-400">Pagado: <span className="text-white font-bold">{totalPagado.toFixed(2)}€</span></p>
                    <p className="text-red-400">Pendiente: <span className="text-white font-bold">{totalSinPagar.toFixed(2)}€</span></p>
                  </div>
                </div>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleCompletarTodos}
                  data-testid="btn-completar-todos"
                >
                  <CheckCircle2 className="h-5 w-5 mr-2" /> Completar Todos
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de pedidos pendientes */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-yellow-500" /> Pendientes ({pedidosPendientes.length})
          </h2>
          
          {pedidosPendientes.length === 0 ? (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="p-8 text-center">
                <ShoppingCart className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay pedidos pendientes</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pedidosPendientes.map(pedido => (
                <Card key={pedido.id} className={`bg-gray-900 border-gray-700 hover:border-gold transition-colors ${pedido.pagado ? 'border-l-4 border-l-green-500' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full">
                            {pedido.nombre_usuario}
                          </span>
                          {pedido.pagado && (
                            <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <DollarSign className="h-3 w-3" /> Pagado
                            </span>
                          )}
                          <span className="text-gray-500 text-sm">
                            {format(new Date(pedido.fecha), "d MMM yyyy HH:mm", { locale: es })}
                          </span>
                        </div>
                        <p className="text-white font-semibold text-lg">
                          {pedido.nombre_producto || 'Producto Prozis'}
                        </p>
                        <a 
                          href={pedido.enlace_producto} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mt-1"
                        >
                          <ExternalLink className="h-3 w-3" /> Ver en Prozis
                        </a>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 text-sm">Cantidad: <span className="text-white">{pedido.cantidad}</span></p>
                        <p className="text-2xl font-bold text-green-400">{(pedido.precio * pedido.cantidad).toFixed(2)}€</p>
                        <div className="flex gap-2 mt-2">
                          {user?.role === 'entrenador' && (
                            <>
                              <Button 
                                size="sm" 
                                className={pedido.pagado ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-500'} 
                                onClick={() => handleTogglePagado(pedido.id)}
                                title={pedido.pagado ? 'Marcar como no pagado' : 'Marcar como pagado'}
                              >
                                <DollarSign className="h-4 w-4" />
                              </Button>
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => handleCompletar(pedido.id)} title="Completar pedido">
                                <Check className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          )}
                          <Button size="sm" variant="destructive" onClick={() => handleEliminar(pedido.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Pedidos completados */}
        {pedidosCompletados.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-400 mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" /> Completados ({pedidosCompletados.length})
            </h2>
            <div className="grid gap-3 opacity-60">
              {pedidosCompletados.slice(0, 10).map(pedido => (
                <Card key={pedido.id} className="bg-gray-900/50 border-gray-800">
                  <CardContent className="p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-500 text-sm">{pedido.nombre_usuario}</span>
                        <p className="text-gray-400">{pedido.nombre_producto || 'Producto Prozis'} x{pedido.cantidad}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-600">{(pedido.precio * pedido.cantidad).toFixed(2)}€</p>
                        <span className="text-xs text-green-500">✓ Completado</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Dialog nuevo pedido */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-gray-900 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" /> Nuevo Pedido Prozis
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white">Enlace del producto *</Label>
                <Input
                  value={formData.enlace_producto}
                  onChange={(e) => setFormData({ ...formData, enlace_producto: e.target.value })}
                  placeholder="https://www.prozis.com/..."
                  className="bg-gray-800 border-gray-600"
                  required
                />
                <p className="text-gray-500 text-xs mt-1">Copia el enlace del producto de Prozis</p>
              </div>
              <div>
                <Label className="text-white">Nombre del producto</Label>
                <Input
                  value={formData.nombre_producto}
                  onChange={(e) => setFormData({ ...formData, nombre_producto: e.target.value })}
                  placeholder="Ej: Whey Protein 1kg Chocolate"
                  className="bg-gray-800 border-gray-600"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Cantidad *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.cantidad}
                    onChange={(e) => setFormData({ ...formData, cantidad: parseInt(e.target.value) || 1 })}
                    className="bg-gray-800 border-gray-600"
                    required
                  />
                </div>
                <div>
                  <Label className="text-white">Precio (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.precio}
                    onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                    placeholder="19.99"
                    className="bg-gray-800 border-gray-600"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1 border-gray-600" onClick={() => setShowDialog(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 btn-primary-mk">
                  <Plus className="h-4 w-4 mr-2" /> Añadir Pedido
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default Pedidos;
